import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface UserCompany {
  id: string;
  company_name: string;
  owner_email: string;
  user_name?: string | null;
  onboarding_completed?: boolean;
  financial_profile?: Record<string, unknown> | null;
  entity_type?: string | null;
  parent_company_id?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  subscription_status?: string | null;
  subscription_plan?: 'monthly' | 'annual' | null;
  trial_start?: string | null;
  trial_end?: string | null;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean | null;
  canceled_at?: string | null;
  payment_failed_at?: string | null;
}

const CACHE_KEY = 'pulse_user_company';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useUserCompany() {
  const { user } = useAuth();
  const [company, setCompany] = useState<UserCompany | null>(() => {
    // Try to load from sessionStorage on mount
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            return data;
          }
        } catch {
          sessionStorage.removeItem(CACHE_KEY);
        }
      }
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserCompany = useCallback(async (forceFresh = false): Promise<UserCompany | null> => {
    try {
      setIsLoading(true);
      setError(null);

      // If forcing fresh data, clear cache first
      if (forceFresh && typeof window !== 'undefined') {
        sessionStorage.removeItem(CACHE_KEY);
      }

      // Get the user's company through the junction table.
      // Use limit(1) + maybeSingle() so users with multiple companies don't hit PGRST116.
      // Prefer the activeCompanyId stored in localStorage (set by EntityContext) if available.
      const storedEntityId = (() => {
        try { return localStorage.getItem('pulse_active_entity'); } catch { return null; }
      })();

      let companyIdToFetch: string | null = null;

      if (storedEntityId) {
        companyIdToFetch = storedEntityId;
      } else {
        const { data: userCompanyData, error: junctionError } = await supabase
          .from('user_companies')
          .select('company_id')
          .eq('user_id', user!.id)
          .limit(1)
          .maybeSingle();

        if (junctionError) throw junctionError;
        companyIdToFetch = userCompanyData?.company_id ?? null;
      }

      if (!companyIdToFetch) {
        setIsLoading(false);
        return null;
      }

      // Get the company details
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select(`
          id,
          company_name,
          owner_email,
          user_name,
          onboarding_completed,
          financial_profile,
          entity_type,
          parent_company_id,
          stripe_customer_id,
          stripe_subscription_id,
          subscription_status,
          subscription_plan,
          trial_start,
          trial_end,
          current_period_start,
          current_period_end,
          cancel_at_period_end,
          canceled_at,
          payment_failed_at
        `)
        .eq('id', companyIdToFetch)
        .single();

      if (companyError) throw companyError;

      setCompany(companyData);

      // Cache the company data
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
          data: companyData,
          timestamp: Date.now()
        }));
      }

      return companyData;
    } catch (err) {
      console.error('Error fetching user company:', err);
      setError(err instanceof Error ? err.message : 'Failed to load company');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const loadCompany = async () => {
      if (!user) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      // If we have cached data that's not stale, use it and finish loading
      if (company) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      // No cached data or stale cache - fetch from database
      // Note: fetchUserCompany handles its own state updates (setCompany, setLoading, setError)
      // The cancelled flag prevents the initial fetch in useEffect from updating state after unmount
      if (!cancelled) {
        await fetchUserCompany();
      }
    };

    loadCompany();

    // Cleanup function prevents state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [user, company, fetchUserCompany]);

  return {
    company,
    isLoading,
    error,
    refetch: () => fetchUserCompany(true) // Always force fresh data on manual refetch, returns Promise<UserCompany | null>
  };
}
